import type { FC, MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import { useAuthStatus, UserAvatar, UserDropdownMenu, useUserDisplayInfo } from '@/features/user'

interface SidebarUserSectionProps {
  readonly isCollapsed?: boolean
}

export const SidebarUserSection: FC<SidebarUserSectionProps> = ({ isCollapsed = false }) => {
  const { user } = useAuthStatus()
  const { displayName, email, initials } = useUserDisplayInfo(user)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const isMenuOpen = Boolean(anchorEl)

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  if (!user) {
    return null
  }

  if (isCollapsed) {
    return (
      <>
        <UserAvatar initials={initials} size="small" onClick={handleOpenMenu} muted />
        <UserDropdownMenu
          anchorEl={anchorEl}
          isOpen={isMenuOpen}
          onClose={handleCloseMenu}
          displayName={displayName}
          email={email}
        />
      </>
    )
  }

  return (
    <>
      <Box component="button" onClick={handleOpenMenu} sx={expandedContainerSx}>
        <UserAvatar initials={initials} size="medium" />
        <Box sx={userInfoSx}>
          <Typography variant="body2" sx={displayNameSx}>
            {displayName}
          </Typography>
          {email && (
            <Typography variant="caption" sx={emailSx}>
              {email}
            </Typography>
          )}
        </Box>
      </Box>
      <UserDropdownMenu
        anchorEl={anchorEl}
        isOpen={isMenuOpen}
        onClose={handleCloseMenu}
        displayName={displayName}
        email={email}
      />
    </>
  )
}

const expandedContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  padding: '8px 0',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 1,
  transition: 'background-color 150ms ease-in-out',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
}

const userInfoSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  overflow: 'hidden',
}

const displayNameSx: SxProps<Theme> = {
  fontWeight: 500,
  color: 'text.primary',
  lineHeight: 1.3,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
}

const emailSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
}
